import {Signal} from "@angular/core"
import {
    TextureEditorDataObjectFragment,
    TextureSetDataFragment,
    TextureSetRevisionDataFragment,
    TextureSetRevisionDescriptorFragment,
} from "@app/textures/service/textures-api.generated"
import {TextureType} from "@generated"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {isKnownOperatorType} from "@app/textures/texture-editor/operators-panel/operator-desc"
import type {OperatorStack} from "@app/textures/texture-editor/operator-stack/operator-stack"

export interface TextureEditorCallback {
    readonly isDebugEnabled: boolean
    readonly $operatorStack: Signal<OperatorStack | undefined>
    readonly $isDataValid: Signal<boolean>
    readonly $isEdited: Signal<boolean>

    setBusy(busy: boolean): void
    setTextureSetRevision(id: string, options?: {warnIfEdited?: boolean; forceReload?: boolean}): void
    closeDialogIfClean(): void
    checkForProcessingEdits(): Promise<void>
    resetForNewLoad(textureSetRevisionChanged: boolean): void
}

export type TextureSetRevisionData = TextureSetRevisionDataFragment & {version: number}

export type TextureTypeData = {
    sourceDataObject?: TextureEditorDataObjectFragment
    resultDataObject?: TextureEditorDataObjectFragment
}

export type TextureSetRevisionSpecific = {
    textureSetRevision: TextureSetRevisionData
    persistedTextureEdits?: TextureEditNodes.TextureEdits
    availableTextureTypes: TextureType[]
    textureSet: TextureSetDataFragment
    sortedTextureSetRevisionDescriptors: (TextureSetRevisionDescriptorFragment & {version: number})[]
    typeData: ReadonlyMap<TextureType, TextureTypeData>
    autoGenerateDisplacementMap: boolean
}

export const getBakedMapDataObjectIds = (data: TextureSetRevisionDataFragment): Map<TextureType, string> => {
    return new Map(data.mapAssignments.map((assignment) => [assignment.textureType, assignment.dataObject.id] as [TextureType, string]))
}

export const getSourceMapDataObjectIds = (data: TextureSetRevisionDataFragment): Map<TextureType, string> => {
    // if we have texture edits, the source maps are stored in the maps object, otherwise we use the map assignments
    const editsJson = data.editsJson as TextureEditNodes.TextureEdits
    if (editsJson) {
        return new Map(Object.entries(editsJson.maps.dataObjectByTextureType).map(([key, value]) => [key, value.dataObjectId]) as [TextureType, string][])
    } else {
        return getBakedMapDataObjectIds(data)
    }
}

export const checkHasValidRatio = (data: TextureSetRevisionDataFragment): boolean => {
    // make sure all maps have the same resolution
    const firstMapResolution = data.mapAssignments
        .map((assignment) => ({
            width: assignment.dataObject.width ?? 0,
            height: assignment.dataObject.height ?? 0,
        }))
        .find(() => true)
    if (!firstMapResolution || firstMapResolution.width === 0 || firstMapResolution.height === 0) {
        return false
    }
    const eps = 1e-4
    const hasSquarePixels = Math.abs(firstMapResolution.width / data.width - firstMapResolution.height / data.height) < eps
    return hasSquarePixels
}

export const checkHasValidResolutions = (data: TextureSetRevisionDataFragment): boolean => {
    // make sure all maps have the same resolution
    const mapAssignmentSizes = data.mapAssignments.map((assignment) => ({
        width: assignment.dataObject.width ?? 0,
        height: assignment.dataObject.height ?? 0,
    }))
    if (mapAssignmentSizes.length === 0) {
        return false
    }
    const firstMapResolution = mapAssignmentSizes[0]
    if (firstMapResolution.width === 0 || firstMapResolution.height === 0) {
        return false
    }
    return mapAssignmentSizes.every((size) => size.width === firstMapResolution.width && size.height === firstMapResolution.height)
}

// detects editsJson referencing an operator type we no longer support (e.g. a removed legacy operator), which can
// no longer be reconstructed into an operator stack - a pure JSON scan, deliberately not going through operator
// deserialization, so it stays safe to call even on data the operator stack itself can't handle anymore
export const editsJsonHasUnsupportedOperator = (editsJson: unknown): boolean => {
    if (!editsJson || typeof editsJson !== "object") {
        return false
    }
    const operators = (editsJson as {operators?: unknown}).operators
    return (
        Array.isArray(operators) &&
        operators.some((op) => {
            const type = op && typeof op === "object" ? (op as {type?: unknown}).type : undefined
            return typeof type !== "string" || !isKnownOperatorType(type)
        })
    )
}
